import React from 'react';

import { Grid, Autocomplete, TextField, Box, Typography, Chip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

function OrOfAndInputComponent({ store }) {
  const orConditions = store((s) => s.data?.condition?.OR) || [];
  const relatedComponents = store((s) => s.relatedComponents) || [];

  const getAvailableOptions = (orIndex, andIndex) => {
    const andConditions = orConditions[orIndex]?.['AND'] || [];
    const selectedIds = andConditions.filter((id, idx) => idx !== andIndex && id && id !== 'null');
    return relatedComponents.filter(comp => !selectedIds.includes(comp.componentId));
  };

  return (
    <Grid item xs={12}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {orConditions.map((orCondition, orConditionindex) => (
          <Box key={orConditionindex}>
            {orConditionindex >= 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 1.5 }}>
                <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    backgroundColor: 'divider',
                    px: 1.5,
                    py: 0.25,
                    borderRadius: '3px',
                    mx: 1.5,
                    fontSize: '0.7rem',
                  }}
                >
                  OR
                </Typography>
                <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
              </Box>
            )}

            <Box
              sx={{
                backgroundColor: 'background.subtle',
                border: '1px solid', borderColor: 'divider',
                borderRadius: '6px',
                p: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mb: 1.5 }}>
                All of these must complete:
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {orCondition?.['AND']?.map((aCondition, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    {index >= 1 && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 600,
                          backgroundColor: 'divider',
                          px: 0.75,
                          py: 0.25,
                          borderRadius: '3px',
                          minWidth: '36px',
                          textAlign: 'center',
                          fontSize: '0.7rem',
                        }}
                      >
                        AND
                      </Typography>
                    )}
                    {index === 0 && <Box sx={{ minWidth: '36px' }} />}

                    <Autocomplete
                      size="small"
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'background.paper',
                          borderRadius: '4px',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.disabled',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.secondary',
                            borderWidth: '1px',
                          },
                        },
                      }}
                      options={getAvailableOptions(orConditionindex, index)}
                      getOptionLabel={option => option.componentTitle || option.name || option.componentId}
                      value={relatedComponents.find(comp => comp.componentId === aCondition) || null}
                      onChange={(event, newValue) => {
                        store.getState().setConditionValue(
                          index,
                          newValue ? newValue.componentId : null,
                          orConditionindex
                        );
                      }}
                      renderInput={params => (
                        <TextField {...params} placeholder="Select a component" />
                      )}
                    />

                    <Box
                      onClick={() => store.getState().deleteCondition(index, orConditionindex)}
                      sx={{
                        cursor: 'pointer',
                        color: 'text.disabled',
                        display: 'flex',
                        alignItems: 'center',
                        p: 0.5,
                        borderRadius: '4px',
                        '&:hover': {
                          color: 'text.secondary',
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box
                onClick={() => store.getState().addCondition(orConditionindex)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 1.5,
                  px: 1.5,
                  py: 0.5,
                  color: 'text.secondary',
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  borderRadius: '4px',
                  border: '1px dashed', borderColor: 'divider',
                  backgroundColor: 'background.paper',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    borderColor: 'text.disabled',
                    backgroundColor: 'background.subtle',
                  },
                }}
              >
                <AddIcon sx={{ fontSize: '1rem' }} />
                Add component
              </Box>
            </Box>
          </Box>
        ))}

        <Box
          onClick={() => store.getState().addORBlock()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            py: 1,
            color: 'text.secondary',
            fontSize: '0.8125rem',
            fontWeight: 500,
            cursor: 'pointer',
            borderRadius: '4px',
            border: '1px dashed', borderColor: 'divider',
            backgroundColor: 'background.paper',
            transition: 'all 0.15s ease',
            '&:hover': {
              borderColor: 'text.disabled',
              backgroundColor: 'background.subtle',
            },
          }}
        >
          <AddIcon sx={{ fontSize: '1rem' }} />
          Add OR condition
        </Box>
      </Box>
    </Grid>
  );
}

function AnyInputComponent({ store }) {
  const conditions = store((s) => s.data?.condition?.OR) || [];
  const relatedComponents = store((s) => s.relatedComponents) || [];

  const getAvailableOptions = (currentIndex) => {
    const selectedIds = conditions.filter((id, idx) => idx !== currentIndex && id && id !== 'null');
    return relatedComponents.filter(comp => !selectedIds.includes(comp.componentId));
  };

  return (
    <Grid item xs={12}>
      <Box
        sx={{
          backgroundColor: 'background.subtle',
          border: '1px solid', borderColor: 'divider',
          borderRadius: '6px',
          p: 2,
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mb: 1.5 }}>
          Continue when any of these complete:
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {conditions.map((aCondition, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {index >= 1 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    backgroundColor: 'divider',
                    px: 0.75,
                    py: 0.25,
                    borderRadius: '3px',
                    minWidth: '28px',
                    textAlign: 'center',
                    fontSize: '0.7rem',
                  }}
                >
                  OR
                </Typography>
              )}
              {index === 0 && <Box sx={{ minWidth: '28px' }} />}

              <Autocomplete
                size="small"
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.paper',
                    borderRadius: '4px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'text.disabled',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'text.secondary',
                      borderWidth: '1px',
                    },
                  },
                }}
                options={getAvailableOptions(index)}
                getOptionLabel={option => option.componentTitle || option.name || option.componentId}
                value={relatedComponents.find(comp => comp.componentId === aCondition) || null}
                onChange={(event, newValue) => {
                  store.getState().setConditionValue(index, newValue ? newValue.componentId : null);
                }}
                renderInput={params => (
                  <TextField {...params} placeholder="Select a component" />
                )}
              />

              <Box
                onClick={() => store.getState().deleteCondition(index)}
                sx={{
                  cursor: 'pointer',
                  color: 'text.disabled',
                  display: 'flex',
                  alignItems: 'center',
                  p: 0.5,
                  borderRadius: '4px',
                  '&:hover': {
                    color: 'text.secondary',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          onClick={() => store.getState().addCondition()}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 1.5,
            px: 1.5,
            py: 0.5,
            color: 'text.secondary',
            fontSize: '0.8125rem',
            fontWeight: 500,
            cursor: 'pointer',
            borderRadius: '4px',
            border: '1px dashed', borderColor: 'divider',
            backgroundColor: 'background.paper',
            transition: 'all 0.15s ease',
            '&:hover': {
              borderColor: 'text.disabled',
              backgroundColor: 'background.subtle',
            },
          }}
        >
          <AddIcon sx={{ fontSize: '1rem' }} />
          Add component
        </Box>
      </Box>
    </Grid>
  );
}

/**
 * AllInputComponent - Read-only display of all connected upstream components.
 * For ALL mode, automatically waits for all connected inputs.
 */
function AllInputComponent({ store }) {
  const relatedComponents = store((s) => s.relatedComponents) || [];

  return (
    <Grid item xs={12}>
      <Box
        sx={{
          backgroundColor: 'background.subtle',
          border: '1px solid', borderColor: 'divider',
          borderRadius: '6px',
          p: 2,
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mb: 1.5 }}>
          Waiting for all connected components
        </Typography>

        {relatedComponents.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
            No upstream components connected yet.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {relatedComponents.map((comp, index) => (
              <Chip
                key={comp.componentId || index}
                label={comp.componentTitle || comp.name || comp.componentId}
                size="small"
                sx={{
                  backgroundColor: 'background.paper',
                  border: '1px solid', borderColor: 'divider',
                  borderRadius: '4px',
                  '& .MuiChip-label': {
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.8125rem',
                  },
                }}
              />
            ))}
          </Box>
        )}

        {relatedComponents.length > 0 && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1.5 }}>
            {relatedComponents.length} component{relatedComponents.length !== 1 ? 's' : ''} must complete
          </Typography>
        )}
      </Box>
    </Grid>
  );
}

export { AnyInputComponent, OrOfAndInputComponent, AllInputComponent };
